import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Ticket, History as HistoryIcon, Loader2 } from 'lucide-react';
import { API } from '../../App';
import { Card } from '../../components/ui/card';

const MyVitalPassPage = () => {
  const [balance, setBalance] = useState(null);
  const [history, setHistory] = useState({ ledger: [], requests: [] });
  const [loading, setLoading] = useState(true);

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } });

  useEffect(() => {
    (async () => {
      try {
        const [bal, hist] = await Promise.all([
          axios.get(`${API}/vitalpass/balance`, authHeader()),
          axios.get(`${API}/vitalpass/history`, authHeader()),
        ]);
        setBalance(bal.data);
        setHistory(hist.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Erro ao carregar');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-teal-600" /></div>;
  }

  if (!balance?.has_vitalpass) {
    return (
      <div className="p-4" data-testid="my-vitalpass-page">
        <h2 className="text-2xl font-black text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Meus VITALPASS
        </h2>
        <Card className="p-8 text-center bg-amber-50 border-amber-200">
          <p className="text-slate-700">Seu plano atual não inclui VITALPASS.</p>
          <p className="text-xs text-slate-500 mt-2">Faça upgrade para um plano <strong>Vital Personal</strong> para receber tokens mensais.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4" data-testid="my-vitalpass-page">
      <h2 className="text-2xl font-black text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
        Meus VITALPASS
      </h2>

      {/* Balance cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <Card className="p-4 bg-gradient-to-br from-teal-600 to-emerald-600 text-white" data-testid="vp-balance-card">
          <div className="flex items-center gap-2 mb-1">
            <Ticket className="w-5 h-5" />
            <p className="text-[10px] uppercase font-semibold tracking-wide">Disponíveis</p>
          </div>
          <p className="text-4xl font-black">{balance.balance}</p>
          <p className="text-xs opacity-90 mt-1">de {balance.total}</p>
        </Card>
        <Card className="p-4">
          <p className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide">Utilizados</p>
          <p className="text-3xl font-black text-slate-900">{balance.used}</p>
        </Card>
        <Card className="p-4">
          <p className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide">Renovação</p>
          <p className="text-sm font-bold text-slate-900">
            {balance.cycle_end ? new Date(balance.cycle_end).toLocaleDateString('pt-BR') : '—'}
          </p>
          <p className="text-[10px] text-slate-500 mt-1">Plano: {balance.plan_name}</p>
        </Card>
      </div>

      {/* History */}
      <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
        <HistoryIcon className="w-5 h-5 text-teal-600" /> Histórico de Utilização
      </h3>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left border-b border-slate-200">
              <th className="py-2 px-3">Data</th>
              <th>Profissional</th>
              <th>Categorias</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody data-testid="vp-history-body">
            {(history.requests || []).length === 0 ? (
              <tr><td colSpan={4} className="text-center py-6 text-slate-500">Nenhuma utilização ainda</td></tr>
            ) : history.requests.map((r) => (
              <tr key={r.request_id} className="border-b border-slate-100">
                <td className="py-2 px-3 text-xs">{new Date(r.requested_at).toLocaleDateString('pt-BR')}</td>
                <td className="font-semibold">{r.professional_name}</td>
                <td className="text-xs">{(r.professional_category_ids || []).join(', ')}</td>
                <td>
                  <span className="text-[10px] uppercase bg-teal-100 text-teal-800 px-2 py-0.5 rounded font-bold">Utilizado</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

export default MyVitalPassPage;
